/**
 * UIPanel - Base class for UI panels
 */

import { Vector2 } from '../math/Vector2';
import { Rectangle } from '../math/Rectangle';

export abstract class UIPanel {
  protected bounds: Rectangle;
  protected isVisible: boolean = false;
  protected isDraggable: boolean = false;
  protected isDragging: boolean = false;
  protected dragOffset: Vector2 = new Vector2(0, 0);
  protected title: string = '';
  protected backgroundColor: string = 'rgba(30, 30, 50, 0.95)';
  protected borderColor: string = '#4ecca3';
  protected titleBarHeight: number = 30;

  constructor(x: number, y: number, width: number, height: number) {
    this.bounds = new Rectangle(x, y, width, height);
  }

  /** Show the panel */
  show(): void {
    this.isVisible = true;
  }

  /** Hide the panel */
  hide(): void {
    this.isVisible = false;
    this.isDragging = false;
  }

  /** Toggle visibility */
  toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /** Check if visible */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /** Set position */
  setPosition(x: number, y: number): void {
    this.bounds = new Rectangle(x, y, this.bounds.width, this.bounds.height);
  }

  /** Center on screen */
  centerOnScreen(screenWidth: number, screenHeight: number): void {
    this.setPosition(
      (screenWidth - this.bounds.width) / 2,
      (screenHeight - this.bounds.height) / 2
    );
  }

  /** Check if point is inside panel */
  containsPoint(pos: Vector2): boolean {
    return this.bounds.contains(pos);
  }

  /** Handle mouse down */
  handleMouseDown(pos: Vector2): boolean {
    if (!this.isVisible) return false;

    if (this.containsPoint(pos)) {
      // Check if clicking title bar for dragging
      if (this.isDraggable && pos.y < this.bounds.y + this.titleBarHeight) {
        this.isDragging = true;
        this.dragOffset = new Vector2(pos.x - this.bounds.x, pos.y - this.bounds.y);
      }
      return true;
    }
    return false;
  }

  /** Handle mouse up */
  handleMouseUp(_pos: Vector2): boolean {
    if (this.isDragging) {
      this.isDragging = false;
      return true;
    }
    return false;
  }

  /** Handle mouse move */
  handleMouseMove(pos: Vector2): boolean {
    if (this.isDragging) {
      this.bounds = new Rectangle(
        pos.x - this.dragOffset.x,
        pos.y - this.dragOffset.y,
        this.bounds.width,
        this.bounds.height
      );
      return true;
    }
    return false;
  }

  /** Handle click (override in subclasses) */
  handleClick(pos: Vector2): boolean {
    if (!this.isVisible || !this.containsPoint(pos)) return false;

    // Check close button
    if (this.isCloseButtonClick(pos)) {
      this.hide();
      return true;
    }

    return this.onPanelClick(pos);
  }

  /** Check if close button was clicked */
  protected isCloseButtonClick(pos: Vector2): boolean {
    const closeX = this.bounds.x + this.bounds.width - 25;
    const closeY = this.bounds.y + 5;
    return pos.x >= closeX && pos.x <= closeX + 20 &&
           pos.y >= closeY && pos.y <= closeY + 20;
  }

  /** Override for custom click handling */
  protected onPanelClick(_pos: Vector2): boolean {
    return true;
  }

  /** Update panel (override in subclasses) */
  update(_deltaTime: number): void {
    // Base implementation does nothing
  }

  /** Render panel */
  render(ctx: CanvasRenderingContext2D): void {
    if (!this.isVisible) return;

    // Background
    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height);

    // Border
    ctx.strokeStyle = this.borderColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height);

    // Title bar
    if (this.title) {
      ctx.fillStyle = this.borderColor;
      ctx.fillRect(this.bounds.x, this.bounds.y, this.bounds.width, this.titleBarHeight);

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(this.title, this.bounds.x + 10, this.bounds.y + 20);
    }

    // Close button
    this.renderCloseButton(ctx);

    // Render content
    this.renderContent(ctx);
  }

  protected renderCloseButton(ctx: CanvasRenderingContext2D): void {
    const x = this.bounds.x + this.bounds.width - 25;
    const y = this.bounds.y + 5;

    ctx.fillStyle = '#e74c3c';
    ctx.fillRect(x, y, 20, 20);

    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x + 5, y + 5);
    ctx.lineTo(x + 15, y + 15);
    ctx.moveTo(x + 15, y + 5);
    ctx.lineTo(x + 5, y + 15);
    ctx.stroke();
  }

  /** Render panel content (override in subclasses) */
  protected abstract renderContent(ctx: CanvasRenderingContext2D): void;

  getBounds(): Rectangle {
    return this.bounds;
  }
}
